/**
 * Endless racer controller: lane snapping, scripted scroll, spawning, HUD hooks,
 * and integration point for Blender OBJ rigs vs procedural fallback meshes.
 */

import * as THREE from "three";
import {
  BUILDING_MAX_ABS_X,
  BUILDING_MIN_ABS_X,
  ENGAGEMENT_RELAX_TAU_SEC,
  LANE_COUNT,
  LANE_X,
  MAX_TRAFFIC_PEAK,
  MAX_TRAFFIC_START,
  PLAYER_DEFAULT_Y,
  PLAYER_FIXED_Z,
  PLAYER_MESH_YAW,
  PROP_MAX_ABS_X,
  PROP_MIN_ABS_X,
  SCENERY_KIND_BUILDING,
  SPEED_AFTER_CRASH_FLOOR,
  SPEED_CAP,
  SPEED_CAP_PEAK,
  SPEED_INITIAL,
  STARTING_LIVES,
  TRAFFIC_VISUAL_SCALE
} from "./constants.js";
import {
  getEngagementProfile,
  variableRatioSpawnJitter
} from "./psychDifficulty.js";
import { createRaceMaterials } from "./materials.js";
import { buildEndlessRaceWorld } from "./worldBuilder.js";
import {
  createAiTrafficVehicle,
  createFallbackPlayerTruckGroup
} from "./proceduralVehicles.js";
import { updateObliqueChaseCamera } from "./cameraRig.js";

/**
 * Hero-only render hedges — articulations trip frustum spheres; OBJ winding sometimes reads “blank” lit only from −Z.
 *
 * @param {THREE.Object3D} root
 */
function applyHeroRenderGuards(root) {
  root.traverse((ch) => {
    if (ch.isMesh) {
      ch.frustumCulled = false;
      const mats = Array.isArray(ch.material) ? ch.material : [ch.material];
      for (const m of mats) {
        if (m && m.isMaterial) m.side = THREE.DoubleSide;
      }
    }
  });
}

/**
 * Normalizes Blender OBJ rigs for the endless runner pivot.
 *
 * **Why auto-yaw matters:** semi meshes often bake **length along +X**. Our camera hugs +Z behind the pivot
 * staring down −Z. If length stays on X the rig renders **paper-thin** (edge‑on invisible) despite correct scale.
 *
 * Steps: detect X‑long footprints, add π/2 so length rides along runway Z → footprint scale → pivot → ground snap.
 *
 * @param {THREE.Object3D} root
 */
function normalizeLoadedPlayerTruck(root) {
  root.rotation.set(0, 0, 0);
  root.updateWorldMatrix(true, true);

  const box0 = new THREE.Box3().setFromObject(root);
  if (box0.isEmpty()) {
    console.warn("[HeavyRigRush] Truck bbox empty — check OBJ load path");
    return;
  }

  const size0 = box0.getSize(new THREE.Vector3());

  let yaw = PLAYER_MESH_YAW;
  if (size0.x > size0.z * 1.08) {
    yaw += Math.PI / 2;
  }
  root.rotation.y = yaw;
  root.updateWorldMatrix(true, true);

  let box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const footprint = Math.max(size.x, size.z, 1e-2);
  const targetFootprint = 7.25;
  root.scale.multiplyScalar(targetFootprint / footprint);

  box.setFromObject(root);
  root.position.sub(box.getCenter(new THREE.Vector3()));
  box.setFromObject(root);
  root.position.y -= box.min.y;

  applyHeroRenderGuards(root);
}

/**
 * @typedef {Object} EndlessRacerHud
 * @property {(score: number) => void} onScoreUi
 * @property {(mph: number) => void} onSpeedUi — display scale matches prototype (~ speed * 3.1)
 * @property {(lives: number) => void} onLivesUi
 * @property {(text: string) => void} onToast
 * @property {(running: boolean) => void} [onRunningChange]
 */

/**
 * @param {Object} opts
 * @param {HTMLElement} opts.mount — WebGL insertion target
 * @param {EndlessRacerHud} opts.hud
 */
export function createEndlessRaceController(opts) {
  const materials = createRaceMaterials();
  const clock = new THREE.Clock();

  const scene = new THREE.Scene();

  /** Scrollable meshes + directional light bookkeeping. */
  const { roadLines, scenery } = buildEndlessRaceWorld(scene, materials);

  const camera = new THREE.PerspectiveCamera(52, 1, 0.1, 180);
  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  opts.mount.innerHTML = "";
  opts.mount.appendChild(renderer.domElement);

  const playerRoot = new THREE.Group();
  scene.add(playerRoot);

  let smoothCamX = 0;
  let laneChangeVelocity = 0;

  const state = {
    running: true,
    lane: 1,
    targetX: LANE_X[1],
    score: 0,
    speed: SPEED_INITIAL,
    lives: STARTING_LIVES,
    spawnTimer: 0,
    /** Alive timer (seconds) feeding difficulty ramp — reset on new run only. */
    runElapsed: 0,
    /** Decays toward 0 — post-crash spawn stress trim (psychDifficulty relief gate). */
    crashRelief: 0
  };

  /**
   * @type {THREE.Group[]}
   */
  const trafficMeshes = [];

  let playerVisual = createFallbackPlayerTruckGroup(materials);
  /** Procedural OBJ stand-in inherits the same base yaw constant as loaded rigs (minus X‑long shim). */
  playerVisual.rotation.y = PLAYER_MESH_YAW;
  playerRoot.add(playerVisual);
  applyHeroRenderGuards(playerVisual);

  function hudSync() {
    opts.hud?.onScoreUi?.(Math.floor(state.score));
    opts.hud?.onSpeedUi?.(Math.floor(state.speed * 3.1));
    opts.hud?.onLivesUi?.(state.lives);
  }

  /** @param {ReturnType<typeof getEngagementProfile>} profile — engagement curves for this frame */
  function spawnTraffic(profile) {
    const dDiff = profile.spawnStress;
    const dynMaxCars = Math.round(THREE.MathUtils.lerp(MAX_TRAFFIC_START, MAX_TRAFFIC_PEAK, dDiff));
    if (trafficMeshes.length >= dynMaxCars) return;

    const usedLanes = new Set(
      trafficMeshes.filter((c) => c.position.z < -42).map((c) => c.userData.lane)
    );
    const available = Array.from({ length: LANE_COUNT }, (_, lane) => lane).filter(
      (lane) => !usedLanes.has(lane)
    );
    const lane = available.length
      ? available[Math.floor(Math.random() * available.length)]
      : Math.floor(Math.random() * LANE_COUNT);

    /** Late runs stack more articulated AI with longer collision hulls. */
    const truckOdds = THREE.MathUtils.lerp(0.24, 0.38, dDiff);
    const isTruckAi = Math.random() < truckOdds;

    const bodyMats = [materials.blue, materials.yellow, materials.aiTruckMetal, materials.black];
    const car = createAiTrafficVehicle(
      materials,
      bodyMats[Math.floor(Math.random() * bodyMats.length)],
      isTruckAi
    );
    car.scale.multiplyScalar(TRAFFIC_VISUAL_SCALE);
    /** Tighter Z spread at go-live — obstacles read sooner in the lane. */
    const spawnStretch = THREE.MathUtils.lerp(28, 17, dDiff);
    car.position.set(LANE_X[lane], PLAYER_DEFAULT_Y, -(102 + Math.random() * spawnStretch));
    car.rotation.y = Math.random() < 0.12 ? Math.PI : 0;

    const offLo = THREE.MathUtils.lerp(3.35, 2.35, dDiff);
    const offHi = THREE.MathUtils.lerp(10.2, 7.4, dDiff);

    const baseLen = isTruckAi ? 3.2 : 2.2;
    car.userData = {
      lane,
      passed: false,
      nearMiss: false,
      length: baseLen * TRAFFIC_VISUAL_SCALE,
      speedOffset: offLo + Math.random() * Math.max(0.25, offHi - offLo)
    };
    scene.add(car);
    trafficMeshes.push(car);
  }

  function crash() {
    /** Full bump — linear decay in `update()` over `ENGAGEMENT_RELAX_TAU_SEC`; trims spawn aggression after impacts. */
    state.crashRelief = 1;
    state.lives -= 1;
    state.speed = Math.max(SPEED_AFTER_CRASH_FLOOR, state.speed * 0.72);
    state.score = Math.max(0, state.score - 120);
    opts.hud.onToast?.("Crash -120");
    playerVisual.rotation.x = -0.18;
    setTimeout(() => {
      playerVisual.rotation.x = 0;
    }, 160);
    if (state.lives <= 0) {
      state.running = false;
      opts.hud?.onRunningChange?.(false);
    }
    hudSync();
  }

  const api = {
    scene,
    camera,
    renderer,
    clock,
    state,
    playerRoot,

    resize() {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / Math.max(h, 1);
      renderer.setSize(w, h);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    },

    /**
     * @param {THREE.Object3D|null} tpl — cloned truck template once OBJ finishes streaming
     */
    setPlayerFromTemplate(tpl) {
      if (!tpl) return;
      playerRoot.clear();
      const clone = tpl.clone(true);
      normalizeLoadedPlayerTruck(clone);
      playerVisual = clone;
      playerRoot.add(playerVisual);
    },

    resetRun() {
      for (const car of trafficMeshes) {
        scene.remove(car);
      }
      trafficMeshes.length = 0;

      Object.assign(state, {
        running: true,
        lane: 1,
        targetX: LANE_X[1],
        score: 0,
        speed: SPEED_INITIAL,
        lives: STARTING_LIVES,
        spawnTimer: 0.52,
        runElapsed: 0,
        crashRelief: 0
      });
      playerRoot.position.set(state.targetX, PLAYER_DEFAULT_Y, PLAYER_FIXED_Z);
      /** Preserve facing (OBJ may include π/2 shim); strip only transient crash/lean rotations. */
      playerVisual.rotation.x = 0;
      playerVisual.rotation.z = 0;
      opts.hud?.onRunningChange?.(true);
      hudSync();
      for (let i = 0; i < 4; i++) {
        spawnTraffic(getEngagementProfile(state.runElapsed, state.crashRelief));
      }
    },

    moveLane(delta) {
      if (!state.running) return;
      state.lane = Math.max(0, Math.min(LANE_COUNT - 1, state.lane + delta));
      state.targetX = LANE_X[state.lane];
      laneChangeVelocity = delta * 18;
    },

    update(dt) {
      dt = Math.min(0.033, dt);

      if (state.running) {
        state.runElapsed += dt;
        state.crashRelief = Math.max(0, state.crashRelief - dt / ENGAGEMENT_RELAX_TAU_SEC);

        /** S-curve flow + layered stress vs speed percept — see psychDifficulty.js. */
        const profile = getEngagementProfile(state.runElapsed, state.crashRelief);

        /** Top speed ladders on the gentler `speedEase` channel (Weber-ish perceived pacing). */
        const capNow = THREE.MathUtils.lerp(SPEED_CAP, SPEED_CAP_PEAK, profile.speedEase);

        /** Accel mixes raw stress so momentum still rebuilds post-crash (relief trims spawns, not dopamine grind). */
        const accelCue = THREE.MathUtils.clamp(
          profile.stress * 0.62 + profile.speedEase * 0.38,
          0,
          1
        );
        const accelBlend = THREE.MathUtils.lerp(1.65, 3.05, accelCue);
        state.speed = Math.min(capNow, state.speed + dt * accelBlend);
        state.score += dt * state.speed * 2.4;

        const diff = state.targetX - playerRoot.position.x;
        const friction = 0.82;
        laneChangeVelocity *= friction;
        playerRoot.position.x += laneChangeVelocity * dt;
        if (Math.abs(diff) > 0.08) {
          playerRoot.position.x += diff * Math.min(1, dt * 16);
        }
        playerVisual.rotation.z = (state.targetX - playerRoot.position.x) * 0.08;

        const travel = state.speed * dt;
        for (const line of roadLines) {
          line.position.z += travel;
          if (line.position.z > 18) line.position.z -= 210;
        }

        for (const item of scenery) {
          item.position.z += travel * 0.9;
          if (item.position.z > 24) {
            item.position.z -= 220;
            let sign = Math.sign(item.position.x);
            if (sign === 0) sign = Math.random() > 0.5 ? 1 : -1;
            const kind = item.userData.sceneryKind;
            const isBuilding = kind === SCENERY_KIND_BUILDING;
            const minAbs = isBuilding ? BUILDING_MIN_ABS_X : PROP_MIN_ABS_X;
            const maxAbs = isBuilding ? BUILDING_MAX_ABS_X : PROP_MAX_ABS_X;
            item.position.x = sign * (minAbs + Math.random() * (maxAbs - minAbs));
          }
        }

        state.spawnTimer -= dt;
        if (state.spawnTimer <= 0) {
          spawnTraffic(profile);
          /** Cadence ramps on relieved spawn stress + variable-ratio jitter (anti-habituation). */
          const s = profile.spawnStress;
          const spawnFloor = THREE.MathUtils.lerp(0.62, 0.35, s);
          const speedWeight = THREE.MathUtils.lerp(0.0052, 0.0102, s);
          const baseNext = Math.max(spawnFloor, 1.92 - state.speed * speedWeight - s * 2.28);
          state.spawnTimer = baseNext * variableRatioSpawnJitter();
        }

        for (let i = trafficMeshes.length - 1; i >= 0; i--) {
          const car = trafficMeshes[i];
          car.position.z += (state.speed - car.userData.speedOffset) * dt;
          car.rotation.z = Math.sin(performance.now() * 0.003 + i) * 0.015;

          const sameLane = Math.abs(car.position.x - playerRoot.position.x) < 0.9;
          const zGap = Math.abs(car.position.z - playerRoot.position.z);

          if (sameLane && zGap < (car.userData.length + 2.2) * 0.45) {
            crash();
            trafficMeshes.splice(i, 1);
            scene.remove(car);
            continue;
          }

          if (
            !car.userData.nearMiss &&
            zGap < 2.5 &&
            Math.abs(car.position.x - playerRoot.position.x) < 1.85 &&
            !sameLane
          ) {
            car.userData.nearMiss = true;
            state.score += 50;
            opts.hud.onToast?.("Near miss +50");
          }

          if (!car.userData.passed && car.position.z > playerRoot.position.z + 4) {
            car.userData.passed = true;
            state.score += 25;
          }

          if (car.position.z > 28) {
            scene.remove(car);
            trafficMeshes.splice(i, 1);
          }
        }
      }

      smoothCamX = updateObliqueChaseCamera(
        camera,
        playerRoot.position,
        smoothCamX,
        dt
      );
      hudSync();
      renderer.render(scene, camera);
    },

    animate() {
      requestAnimationFrame(() => api.animate());
      api.update(clock.getDelta());
    }
  };

  api.resetRun();
  api.resize();
  api.animate();

  return api;
}
